import { message } from 'antd';
import { getLoadErrorMessage, getSaveErrorMessage, getDeleteErrorMessage, getErrorMessage } from '../../utils/errorUtils';

/**
 * 공통 에러 메시지 컴포넌트
 * 
 * 이 컴포넌트는 데이터 로딩, 저장, 삭제 실패 시 일관된 에러 메시지를 표시합니다.
 */

/**
 * 데이터 로딩 실패 에러 메시지를 표시합니다.
 * 
 * @param dataType - 데이터 타입 (예: '규범류', '공지사항', '입법예고' 등)
 */
export const showLoadError = (dataType: string) => {
  message.error(getLoadErrorMessage(dataType));
};

/**
 * 데이터 저장 실패 에러 메시지를 표시합니다.
 * 
 * @param dataType - 데이터 타입 (예: '규범류', '공지사항', '입법예고' 등)
 */
export const showSaveError = (dataType: string) => {
  message.error(getSaveErrorMessage(dataType));
};

/**
 * 데이터 삭제 실패 에러 메시지를 표시합니다.
 * 
 * @param dataType - 데이터 타입 (예: '규범류', '공지사항', '입법예고' 등)
 */
export const showDeleteError = (dataType: string) => {
  message.error(getDeleteErrorMessage(dataType));
};

/**
 * 일반적인 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 * @param defaultMessage - 기본 에러 메시지 (선택사항)
 */
export const showError = (error: any, defaultMessage?: string) => {
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || defaultMessage || '오류가 발생했습니다.');
};

/**
 * 승인 관련 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 * @param action - 액션 타입 ('approve' | 'unapprove')
 */
export const showApprovalError = (error: any, action: 'approve' | 'unapprove' = 'approve') => {
  const actionText = action === 'approve' ? '승인' : '승인 취소';
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || `${actionText}에 실패했습니다.`);
};

/**
 * 파일 업로드 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 * @param fileType - 파일 타입 (예: '첨부파일', '이미지' 등)
 */
export const showUploadError = (error: any, fileType: string = '파일') => {
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || `${fileType} 업로드에 실패했습니다.`);
};

/**
 * 파일 다운로드 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 * @param fileType - 파일 타입 (예: '첨부파일', '문서' 등)
 */
export const showDownloadError = (error: any, fileType: string = '파일') => {
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || `${fileType} 다운로드에 실패했습니다.`);
};

/**
 * 댓글 관련 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 * @param action - 액션 타입 ('create' | 'update' | 'delete')
 */
export const showCommentError = (error: any, action: 'create' | 'update' | 'delete' = 'create') => {
  const actionText = {
    create: '댓글 작성',
    update: '댓글 수정',
    delete: '댓글 삭제'
  }[action];
  
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || `${actionText}에 실패했습니다.`);
};

/**
 * 검색 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 */
export const showSearchError = (error: any) => {
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || '검색에 실패했습니다.');
};

/**
 * 상태 변경 에러 메시지를 표시합니다.
 * 
 * @param error - 에러 객체
 * @param statusType - 상태 타입 (예: '승인', '삭제' 등)
 */
export const showStatusChangeError = (error: any, statusType: string = '상태') => {
  const errorMessage = getErrorMessage(error);
  message.error(errorMessage || `${statusType} 변경에 실패했습니다.`);
}; 